import { Button, BUTTON_STATUS, SIZE } from '@dehaat/dds';
import uploadToS3Bucket from '@dehaat/kisan-app-bl/api/uploadToS3Bucket';
import { BUCKET_TYPE } from '@dehaat/kisan-app-bl/models/api/FileUpload';
import Image, { StaticImageData } from 'next/image';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { ChangeEvent, useContext, useMemo, useState } from 'react';
import { COOKIE_MAX_AGE_1YEAR, LAT_LNG_COOKIE_NAME } from '@/constants/common';
import { UserContext } from '@/context/UserProvider';
import { useAppSelector } from '@/hooks/reduxHooks';
import useErrorModalState from '@/hooks/useErrorModalState';
import useTriggerEvents from '@/hooks/useTriggerEvents';
import BottomLeftCorner from '@/images/prakshep/bottom-left-corner.webp';
import BottomRightCorner from '@/images/prakshep/bottom-right-corner.webp';
import TopLeftCorner from '@/images/prakshep/top-left-corner.webp';
import TopRightCorner from '@/images/prakshep/top-right-corner.webp';
import { selectLatLng } from '@/store/features/locationSlice';
import CustomEventTypes from '@/utils/analytics/customEventTypes';
import { getCurrentLocationValues, LocationType } from '@/utils/google';
import CenterModal from '../CenterModal';
import ErrorModal from '../ErrorModal';
import HeaderWithBackButton from '../HeaderWithBackButton';
import TextField from '../input/TextField';
import Okay from '../modalActions/Okay';
import Spinner from '../Spinner';
import TakeCurrentLocation from './TakeCurrentLocation';
export type CornerData = {
  file?: Blob;
  filepath?: string;
  location?: LocationType;
  preview?: string;
};
interface CornerCardProps {
  id: number;
  title: string;
  imageSrc: StaticImageData | string;
  preview?: string;
  onChange: (id: number, e: ChangeEvent<HTMLInputElement>) => void;
  onRemove: (id: number) => void;
}
const CornerCard = ({
  id,
  title,
  imageSrc,
  preview,
  onChange,
  onRemove
}: CornerCardProps) => {
  return <div className="rounded-2xl p-2 bg-gray-20" data-sentry-component="CornerCard" data-sentry-source-file="TakeFarmFourCornersPhoto.tsx">
      <p className="font-normal text-xs text-[#171B1F] mb-1">{title}</p>
      <input id={`corner-${id}`} type="file" accept=".jpg,.jpeg,.png" capture="environment" className="hidden" onChange={e => onChange(id, e)} />

      <div className="relative w-full aspect-[2/1] min-h-[160px] overflow-hidden rounded-lg">
        <label htmlFor={`corner-${id}`} className="block w-full h-full">
          <Image src={preview || imageSrc} alt="corner" fill className="object-cover" //object-contain
        data-sentry-element="Image" data-sentry-source-file="TakeFarmFourCornersPhoto.tsx" />
        </label>
        {preview && <button type="button" onClick={() => onRemove(id)} className="absolute top-1 right-2 bg-error-100 text-white rounded-full w-6 h-6 flex items-center justify-center">
            ✕
          </button>}
      </div>
    </div>;
};
const corners = [{
  id: 1,
  title: 'Corner 1',
  image: TopLeftCorner
}, {
  id: 2,
  title: 'Corner 2',
  image: TopRightCorner
}, {
  id: 3,
  title: 'Corner 3',
  image: BottomLeftCorner
}, {
  id: 4,
  title: 'Corner 4',
  image: BottomRightCorner
}];
interface Props {
  onContinue: (farmName: string, images: Record<number, CornerData>) => void;
  onBackClick: VoidFunction;
}
const TakeFarmFourCornersPhoto = ({
  onBackClick,
  onContinue
}: Props) => {
  const {
    t
  } = useTranslation('satellite');
  const [farmName, setFarmName] = useState('');
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [isfileUploading, setIsFileUploading] = useState(false);
  const [location, setLocation] = useState<LocationType>();
  const [showFarmImage, setShowFarmImage] = useState(false);
  const [pendingCornerId, setPendingCornerId] = useState<number | null>(null);
  const {
    errorModalState,
    hideErrorModal,
    setErrorModalState
  } = useErrorModalState();
  const [cornerImages, setCornerImages] = useState<Record<number, CornerData>>({});
  const {
    query: {
      sh
    }
  } = useRouter();
  const {
    user
  } = useContext(UserContext);
  const onChange = async (id: number, e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;
    const file = e.target.files[0];
    try {
      setIsFileUploading(true);
      const filepath = await uploadToS3Bucket({
        file,
        filePath: `PRAKSHEP/${user?.id}/`,
        bucketType: 'private' as BUCKET_TYPE
      });
      const existingLocation = await getCurrentLocationValues();
      setCornerImages(prev => ({
        ...prev,
        [id]: {
          file,
          filepath,
          location: existingLocation || undefined,
          preview: URL.createObjectURL(file)
        }
      }));
      if (!existingLocation) {
        setPendingCornerId(id);
        setShowLocationModal(true);
      }
    } catch {
      setErrorModalState({
        ...errorModalState,
        message: t('image_upload_failed'),
        show: true
      });
    } finally {
      setIsFileUploading(false);
      e.target.value = '';
    }
  };
  const removeImage = (id: number) => {
    setCornerImages(prev => {
      const updated = {
        ...prev
      };
      delete updated[id];
      return updated;
    });
  };
  const isNextEnabled = Object.values(cornerImages).filter(corner => corner.filepath && corner.location).length >= 3;
  const firstCornerPreview = useMemo(() => {
    const file = Object.values(cornerImages)[0]?.file;
    return file ? URL.createObjectURL(file) : '';
  }, [cornerImages]);
  const isContinueButtonDisable = farmName?.length < 3;
  const latLng = useAppSelector(selectLatLng);
  const handleContinue = () => {
    onContinue(farmName, cornerImages);
  };
  return errorModalState?.show ? <ErrorModal {...errorModalState} onClose={hideErrorModal} data-sentry-element="ErrorModal" data-sentry-component="TakeFarmFourCornersPhoto" data-sentry-source-file="TakeFarmFourCornersPhoto.tsx">
      <Okay handleClick={hideErrorModal} data-sentry-element="Okay" data-sentry-source-file="TakeFarmFourCornersPhoto.tsx" />
    </ErrorModal> : <>
      {!showFarmImage ? <main className="top-0 left-0 z-50 fixed bg-white w-full h-screen overflow-y-auto flex flex-col">
          {sh != 'f' && <HeaderWithBackButton heading={'Farm location'} showHomeIcon={false} showProfileIcon={false} onBackClick={() => onBackClick()} />}
          {isfileUploading && <Spinner />}

          <div className=" mt-4 pt-16 px-4 pb-6">
            <p className="text-neutral-90 text-lg font-semibold">
              Click photo from corners
            </p>
            <p className="text-sm font-normal text-neutral-text-light-70 mb-4">
              Go to each corner of farm and click photo of farm
            </p>
            <div className="grid grid-cols-2 gap-4">
              {corners.map(c => <CornerCard key={c.id} id={c.id} title={c.title} imageSrc={c.image} preview={cornerImages[c.id]?.preview} onChange={onChange} onRemove={removeImage} />)}
            </div>
          </div>
          {showLocationModal && <CenterModal show={showLocationModal}>
              <TakeCurrentLocation getCurrentLatLong={location => {
          setLocation(location);
          if (pendingCornerId !== null) {
            setCornerImages(prev => ({
              ...prev,
              [pendingCornerId]: {
                ...prev[pendingCornerId],
                location: location
              }
            }));
          }
          if (!latLng) {
            document.cookie = `${LAT_LNG_COOKIE_NAME}=${`${location.latitude},${location.longitude}`}; max-age=${COOKIE_MAX_AGE_1YEAR}; path=/`;
          }
          setPendingCornerId(null);
          setShowLocationModal(false);
        }} />
            </CenterModal>}
          <div className="flex-1 bg-gray-20"></div>
          {isNextEnabled && <div className="bg-white absolute bottom-0 right-0 left-0 p-4 transition-shadow">
              <Button onClick={() => setShowFarmImage(true)} text="Next" size={SIZE.large} />
            </div>}
        </main> : <main className="top-0 left-0 z-50 fixed bg-white w-full h-screen overflow-y-auto">
          {sh != 'f' && <HeaderWithBackButton heading={'Farm details'} showHomeIcon={false} showProfileIcon={false} onBackClick={() => setShowFarmImage(false)} />}
          <section className={`p-4 ${sh != 'f' ? 'pt-16' : 'pt-8'}`}>
            <Image src={firstCornerPreview} alt="farm-image" width={800} height={600} className="m-auto mt-4 mb-8 rounded-lg max-h-[50vh] w-auto h-auto object-contain" />

            <TextField value={farmName} label={t('farm_name')} name="name" onChange={e => setFarmName(e.target.value)} required />
          </section>
          <footer className="bottom-0 left-0 fixed bg-white shadow-[0_-2px_6px_rgba(0,0,0,0.10)] p-4 w-full">
            <Button onClick={handleContinue} text={t('continue')} size={3} status={!isContinueButtonDisable ? BUTTON_STATUS.ACTIVE : BUTTON_STATUS.DISABLED} />
          </footer>
        </main>}
    </>;
};
export default TakeFarmFourCornersPhoto;